import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Breadcrumbs, { buildSettingsBreadcrumbs } from '../../components/Breadcrumbs';
import Alert from '../../components/Alert';
import ConfirmModal from '../../components/ConfirmModal';
import { SkeletonForm } from '../../components/SkeletonLoader';

function SquareSettings() {
  const navigate = useNavigate();
  const firstInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [credentials, setCredentials] = useState({
    applicationId: '',
    accessToken: '',
    locationId: '',
    environment: 'sandbox'
  });

  const [hasExistingCredentials, setHasExistingCredentials] = useState(false);
  const [configuredAt, setConfiguredAt] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  // #19 - AutoFocus on first input when loaded
  useEffect(() => {
    if (!loading && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [loading]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/settings/square`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.hasCredentials) {
        setCredentials({
          applicationId: response.data.applicationId || '',
          accessToken: '',
          locationId: response.data.locationId || '',
          environment: response.data.environment || 'sandbox'
        });
        setHasExistingCredentials(true);
        setConfiguredAt(response.data.configuredAt);
      }
    } catch (err) {
      setError('Failed to load settings');
      console.error('Load Square settings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
    setTestResult(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');

      if (!credentials.applicationId || !credentials.locationId) {
        setError('Application ID and Location ID are required');
        setSaving(false);
        return;
      }

      if (!credentials.accessToken && !hasExistingCredentials) {
        setError('Access Token is required for initial setup');
        setSaving(false);
        return;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/settings/square`,
        credentials,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Square credentials saved successfully');
      setHasExistingCredentials(true);
      setCredentials(prev => ({ ...prev, accessToken: '' }));
      fetchSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/settings/square`,
        { applicationId: '', accessToken: '', locationId: '', environment: 'sandbox' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCredentials({
        applicationId: '',
        accessToken: '',
        locationId: '',
        environment: 'sandbox'
      });
      setHasExistingCredentials(false);
      setConfiguredAt(null);
      setTestResult(null);
      setSuccess('Square credentials cleared. Online payments are now disabled until you reconfigure.');
    } catch (err) {
      setError('Failed to clear credentials');
    } finally {
      setSaving(false);
      setShowClearConfirm(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setTestResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const testData = {
        applicationId: credentials.applicationId,
        locationId: credentials.locationId,
        environment: credentials.environment
      };

      if (credentials.accessToken) {
        testData.accessToken = credentials.accessToken;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/settings/square/test`,
        testData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult({
        success: true,
        message: response.data.message,
        location: response.data.location
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-hub-header">
          <Breadcrumbs items={buildSettingsBreadcrumbs('Payment Settings')} />
          <button onClick={() => navigate('/admin/settings-hub')} className="admin-hub-back-btn">
            <span className="admin-hub-back-arrow">←</span>
            Back to Settings
          </button>
          <h1 className="admin-page-title">Payment Settings</h1>
        </div>
        <div className="card">
          <SkeletonForm fields={4} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header with Breadcrumbs */}
      <div className="admin-hub-header">
        <Breadcrumbs items={buildSettingsBreadcrumbs('Payment Settings')} />
        <button onClick={() => navigate('/admin/settings-hub')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <h1 className="admin-page-title">Payment Settings</h1>
        <p className="admin-hub-subtitle">Configure your Square payment credentials to receive customer payments directly</p>
      </div>

      {/* Warning Banner - Not Configured */}
      {!hasExistingCredentials && (
        <div className="card card-warning" style={{ marginBottom: '25px', padding: '15px 20px' }}>
          <div className="alert-content" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span className="alert-icon">⚠️</span>
            <div>
              <strong className="alert-title" style={{ color: 'var(--color-warning-text)' }}>Payment processing is not active</strong>
              <p style={{ margin: '8px 0 0', color: 'var(--color-warning-text)' }}>
                Your customers cannot make online payments until you configure your Square credentials below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="card card-info" style={{ marginBottom: '25px', padding: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-brand-text)' }}>How Payment Processing Works</h3>
        <p style={{ margin: '0 0 10px 0', color: 'var(--color-text-light)' }}>
          When customers make loan payments on your site, funds go directly to your Square account.
          You must configure your Square credentials to accept online payments from customers.
        </p>
        <p className="form-hint">
          You'll need a <a href="https://squareup.com/signup" target="_blank" rel="noopener noreferrer">Square Developer Account</a> to
          get your Application ID, Access Token, and Location ID.
        </p>
      </div>

      {/* Status Card */}
      {hasExistingCredentials && (
        <div className="card card-success" style={{ marginBottom: '25px', padding: '15px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.5rem' }}>✅</span>
            <div>
              <strong style={{ color: 'var(--color-success-text)' }}>Square Credentials Configured</strong>
              {configuredAt && (
                <p style={{ margin: '5px 0 0', fontSize: '0.85rem', color: 'var(--color-success-text)' }}>
                  Last updated: {new Date(configuredAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="card">
        <form onSubmit={handleSave}>
          {/* #27 - Dismissible alerts */}
          {error && (
            <Alert type="error" message={error} onDismiss={() => setError('')} />
          )}

          {success && (
            <Alert type="success" message={success} onDismiss={() => setSuccess('')} />
          )}

          <div className="form-group">
            <label className="form-label">Application ID *</label>
            <input
              ref={firstInputRef}
              type="text"
              name="applicationId"
              value={credentials.applicationId}
              onChange={handleChange}
              placeholder="sq0idp-XXXXXXXXXXXX"
              className="form-input"
            />
            <p className="form-hint">
              Found in your Square Developer Dashboard under Applications
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">
              Access Token {hasExistingCredentials ? '(leave blank to keep current)' : '*'}
            </label>
            <input
              type="password"
              name="accessToken"
              value={credentials.accessToken}
              onChange={handleChange}
              placeholder={hasExistingCredentials ? '••••••••••••••••' : 'EAAAxxxxxxxxxxxxxxxx'}
              className="form-input"
            />
            <p className="form-hint">
              Your personal access token from Square Developer Dashboard. This is encrypted before storage.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Location ID *</label>
            <input
              type="text"
              name="locationId"
              value={credentials.locationId}
              onChange={handleChange}
              placeholder="LXXXXXXXXXXXX"
              className="form-input"
            />
            <p className="form-hint">
              Found in Square Dashboard under Account & Settings → Business → Locations
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Environment</label>
            <select
              name="environment"
              value={credentials.environment}
              onChange={handleChange}
              className="form-select"
            >
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="production">Production (Live Payments)</option>
            </select>
            <p className="form-hint">
              Use Sandbox for testing. Switch to Production only when ready to accept real payments.
            </p>
          </div>

          {/* Test Result */}
          {testResult && (
            <Alert
              type={testResult.success ? 'success' : 'error'}
              title={testResult.message}
              message={testResult.location ? `Location: ${testResult.location.name} (${testResult.location.status})` : ''}
              onDismiss={() => setTestResult(null)}
            />
          )}

          {/* #14 - Buttons: Cancel/Secondary left, Primary right */}
          <div className="modal-footer-buttons" style={{ marginTop: '25px' }}>
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || (!credentials.applicationId && !hasExistingCredentials)}
              className="btn btn-secondary"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            {hasExistingCredentials && (
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                disabled={saving}
                className="btn btn-danger"
              >
                Clear Credentials
              </button>
            )}

            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save Credentials'}
            </button>
          </div>
        </form>
      </div>

      {/* #12 - Custom Confirm Modal instead of window.confirm */}
      <ConfirmModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClear}
        title="Clear Square Credentials?"
        message="Your customers will not be able to make online payments until you reconfigure."
        confirmText="Clear Credentials"
        cancelText="Cancel"
        type="danger"
        loading={saving}
      />
    </div>
  );
}

export default SquareSettings;
